import { useCallback } from 'react'
import posthog from 'posthog-js'

import { useAnalytics } from '@percent/cause-dashboard/common/hooks/useAnalytics/useAnalytics'
import {
  mergeUtmParams,
  parseUtmParamsFromSearch,
  readStoredUtmParams
} from '@percent/cause-dashboard/common/hooks/useUtmParams/useUtmParams'
import { env } from '@percent/cause-dashboard/env'
import { getDeviceType } from '@percent/cause-dashboard/utils/deviceType'
import { getGfmExperimentContext, setGfmExperimentVariant } from '../useGfmExperiment/gfmExperiment'
import { toCampaignContext, toUtmProperties } from './campaignProperties'

const TOKEN_PARAM = 'token='

const redactToken = (text: string) => {
  if (!text) {
    return ''
  }

  const tokenIndex = text.search(TOKEN_PARAM)

  return tokenIndex === -1 ? text : text.substring(0, tokenIndex + TOKEN_PARAM.length).concat('redacted')
}

type PageProperties = {
  url: string
  search: string
  referrer: string
}

const getPageProperties = (): PageProperties => ({
  url: redactToken(window?.location?.href),
  search: redactToken(window?.location?.search),
  referrer: redactToken(document?.referrer)
})

const defaultOptions = { domain: 'Causes portal' }

const getBaseContext = (pageProperties: PageProperties) => ({
  app: { name: 'Causes portal', id: env.REACT_APP_VERCEL_GIT_COMMIT_SHA },
  device: { type: getDeviceType() },
  ip: '0.0.0.0',
  ...pageProperties,
  page: {
    ...pageProperties
  },
  /*
    NGM-180: the running experiment, in context rather than properties so Segment writes it to
    every event table as context_experiment_name / context_experiment_variant. Empty until the
    variant resolves.
  */
  ...getGfmExperimentContext()
})

export const useCausesPortalAnalytics = () => {
  const analytics = useAnalytics()

  const buildEventPayload = useCallback(() => {
    const pageProperties = getPageProperties()
    const utmParams = mergeUtmParams(parseUtmParamsFromSearch(window?.location?.search ?? ''), readStoredUtmParams())
    const campaignContext = toCampaignContext(utmParams)

    return {
      properties: { ...pageProperties, ...toUtmProperties(utmParams) },
      context: {
        ...getBaseContext(pageProperties),
        ...(Object.keys(campaignContext).length > 0 ? { campaign: campaignContext } : {})
      }
    }
  }, [])

  const page = useCallback(
    (page: string, options?: Record<string, unknown>) => {
      const { properties, context } = buildEventPayload()

      void analytics?.page(
        'Page',
        page,
        {
          ...defaultOptions,
          ...options,
          ...properties
        },
        { context }
      )

      if (env.NX_PUBLIC_POSTHOG_API_KEY) {
        posthog.capture('$pageview', { page, ...defaultOptions, ...options, ...properties })
      }
    },
    [analytics, buildEventPayload]
  )

  const identify = useCallback(
    ({ id, traits }: { id?: string; traits?: Record<string, unknown> }) => {
      const { context } = buildEventPayload()

      void analytics?.identify(id, { ...traits }, { context })

      if (env.NX_PUBLIC_POSTHOG_API_KEY) {
        if (id) {
          posthog.identify(id, { ...traits })
        }

        if (traits?.organisationId) {
          posthog.group('company', traits.organisationId as string, {
            name: traits?.organisation as string | undefined,
            country: traits?.country as string | undefined
          })
        }
      }
    },
    [analytics, buildEventPayload]
  )

  const track = useCallback(
    (name: string, options?: Record<string, unknown>) => {
      const { properties, context } = buildEventPayload()

      void analytics?.track(
        name,
        {
          ...defaultOptions,
          ...options,
          ...properties
        },
        { context }
      )

      if (env.NX_PUBLIC_POSTHOG_API_KEY) {
        posthog.capture(name, { ...defaultOptions, ...options, ...properties })
      }
    },
    [analytics, buildEventPayload]
  )

  const reset = useCallback(() => {
    // Drop the experiment too, so a later session cannot inherit the previous user's variant
    setGfmExperimentVariant(null)
    analytics?.reset()

    if (env.NX_PUBLIC_POSTHOG_API_KEY) {
      posthog.reset()
    }
  }, [analytics])

  return {
    page,
    identify,
    track,
    reset,
    analyticsLoaded: !!analytics
  }
}
